// The UI and API are served **same-origin** behind one CloudFront distribution
// (see ADR-1005). Every API call hits `/api/*` on the current origin — no separate
// `api.` subdomain, no cross-origin request, so no CORS. Same-origin is required: the
// gate is the HttpOnly `cai_session` cookie (SameSite=Strict), which is only sent
// same-origin. Gated calls pass `credentials: 'include'` so the cookie rides along.
//
// In local development the Vite dev server proxies `/api` to VITE_API_URL (the local
// dev API server — see vite.config.ts + src/dev-server), so the full login + gate flow
// runs on localhost with no CloudFront and no corporate proxy in the path.
export const API_BASE = '/api';

/** Build an absolute-on-origin API path, e.g. apiUrl('/auth') -> '/api/auth'. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

/**
 * Liveness probe for the whole CloudFront → WAF → API Gateway path.
 *
 * Hits the public `GET /api/health` MOCK integration (no Lambda, no cold start).
 * Returns true only on a 2xx response; any network error or non-2xx is treated as
 * down. Callers gate user actions (login/subscribe/unsubscribe) on this.
 */
export async function checkHealth(signal?: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch(apiUrl('/health'), {
      method: 'GET',
      // Never serve a stale cached body — we want the live edge→API state.
      cache: 'no-store',
      signal
    });
    return res.ok;
  } catch {
    return false;
  }
}
